import React, {ReactNode, useEffect, useRef} from 'react';

import axios, {AxiosResponse, CancelToken, CancelTokenSource} from 'axios';
import memoize from 'memoize-one';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import {InlineAlert} from '@/components/alerts';
import Button from '@/components/ui/Button';
import {produce} from 'immer';
import {
    chartHeightValues,
    chartTimelineIntervals,
    ChartType,
    hierarchicalAggregationChartTypes,
    chartWidthValues,
    defaultIntervalUnit,
} from '../utils/chartConstants';
import CellLoader from '@/components/loading/CellLoader';
import limiter from '@/utils/requests';
import usePreviousValue from '@/utils/usePreviousValue';
import useGlobalFiltersCombined from '@/views/storyboard/filters/useGlobalFiltersCombined';
import mergeLocalAndGlobalFilters from '@/views/storyboard/filters/mergeLocalAndGlobalFilters';
import {AdditionalFilter, AllFields} from 'entities/chartFilters';
import {AggregationType, BarOrientation, BarSortOrder, ChartIntervalUnit, ChartOutput, ChartQuery, DataLabelPosition} from 'entities/chart';
import {ChartResponseData} from 'entities/chartData';
import {resolveRestrictedFields} from '@/utils/fieldRegistry';
import {WithTranslation} from 'react-i18next';
import {serialize} from '@/views/storyboard/utils/helpers';
import {sanitizeChartQueryForRequest} from '@/views/storyboard/utils/sanitizeChartQuery';
import i18n from 'i18n';
import {ChartFooterData} from '@/views/storyboard/charts/utils/chartFooterData';

interface ChartUpdateRequestParams {
    params: ChartQuery;
    cancelToken: CancelToken | undefined;
}

interface SerializedChartUpdateRequestParams {
    params: Omit<ChartQuery, 'all_fields' | 'additionalFilters'> & {
        all_fields: string;
        additionalFilters: string;
    };
    cancelToken: CancelToken | undefined;
}


const chartUpdateRequest = limiter.wrap(
    (url: string, params: SerializedChartUpdateRequestParams) => {
        // Use custom serialization for URL parameters to handle aggs_item_list correctly
        const queryString = serialize(params.params);
        const fullUrl = `${url}?${queryString}`;

        return axios.get<ChartResponseData | string>(fullUrl, {
            cancelToken: params.cancelToken
        })
            .then((response) => {
                return response;
            })
            .catch((error) => {
                if (error.response) {
                    return error.response;
                }

                // Check if this is a cancellation error
                if (axios.isCancel(error)) {
                    return null;
                }

                console.warn('Chart data request failed:', error.message || error);
                console.warn('Request URL:', fullUrl);
                return null;
            });
    }
);

const chartUpdateRequestDebounced = AwesomeDebouncePromise(chartUpdateRequest, 350, {
    key: (url: string, params: SerializedChartUpdateRequestParams) => `${url}-${JSON.stringify(params.params)}`,
});

const chartSettingsPreviewRequestDebounced = AwesomeDebouncePromise(chartUpdateRequest, 600, {
    key: (url: string, params: SerializedChartUpdateRequestParams) => `${url}-${JSON.stringify(params.params)}`,
});



interface FilterChartRefreshPluginProps {
    loadData: (debounce: boolean, globalFields: AllFields, globalFilters: AdditionalFilter[]) => void;
    chartQuery: ChartQuery;
    chartType: ChartType;
    querySignature: string;
}

const QUERY_TYPES_WITHOUT_AGG_FIELDS: ChartType[] = [
    ChartType.Wordcloud,
    ChartType.TextSamples,
    ChartType.Concordance,
    ChartType.TrendingWords,
];

const GlobalFilterChartRefreshPlugin = React.memo<FilterChartRefreshPluginProps>((
    {
        loadData,
        chartQuery,
        chartType,
        querySignature,
    },
) => {
    // Just a random helper component because CellHandler is a class-based component and can't access easy-peasy state.
    // If all CellHandlers are converted to functional components, then this component is obsolete.

    const [globalAllFields, globalAdditionalFilters] = useGlobalFiltersCombined();

    const loaded = useRef(false);

    const previousChartType = usePreviousValue(chartType, true);
    const previousChartQuery = usePreviousValue(chartQuery, true);
    const previousQuerySignature = usePreviousValue(querySignature, true);
    const previousGlobalAdditionalFilters = usePreviousValue(globalAdditionalFilters, true);
    const previousGlobalAllFields = usePreviousValue(globalAllFields, true);

    useEffect(() => {

        // Basically the only thing that this effect does is reload the charts based on the ruleset below.
        // If charts reload too often/not often enough, then the problem is probably here:

        // A new filter was added but has no value yet so don't run the search again
        // TODO Right now issue when duplicating and adding new filters with this solution
        // if (previousChartQuery?.additionalFilters?.length < chartQuery.additionalFilters?.length) {
        //     return;
        // }

        if (previousGlobalAdditionalFilters?.length < globalAdditionalFilters?.length) {
            return;
        }
        if (previousGlobalAdditionalFilters !== globalAdditionalFilters
            && !previousGlobalAdditionalFilters?.length && !globalAdditionalFilters?.length) {
            return;
        }
        if (previousGlobalAllFields !== globalAllFields
            && globalAllFields
            && previousGlobalAllFields
            && !Object.keys(previousGlobalAllFields).length
            && !Object.keys(globalAllFields).length
        ) {
            return;
        }

        // An empty filter was removed that has no effect on the query
        let emptyRemoved = false;
        if (previousGlobalAdditionalFilters?.length === globalAdditionalFilters?.length + 1) {
            previousGlobalAdditionalFilters.forEach((el) => {
                const key = el.value.split('.')[1];
                if (
                    !globalAdditionalFilters.find((x) => x.value === el.value)
                    && !previousGlobalAllFields[key]?.length
                ) {
                    emptyRemoved = true;
                }
            });
        }
        if (previousChartQuery?.additionalFilters?.length === chartQuery.additionalFilters?.length + 1) {
            previousChartQuery.additionalFilters.forEach((el) => {
                const key = el.value.split('.')[1];
                if (
                    !chartQuery.additionalFilters.find((x) => x.value === el.value)
                    && !previousChartQuery.all_fields?.[key]?.length
                ) {
                    emptyRemoved = true;
                }
            });
        }
        if (emptyRemoved) {
            return;
        }

        const globalFiltersChanged = previousGlobalAdditionalFilters !== globalAdditionalFilters
            || previousGlobalAllFields !== globalAllFields;

        const chartTypeChanged = previousChartType !== chartType;

        if (previousQuerySignature === querySignature && loaded.current && !globalFiltersChanged && !chartTypeChanged) {
            return;
        }

        if (!chartQuery.query_string) {
            return;
        }

        loadData(!loaded.current, globalAllFields, globalAdditionalFilters);
    }, [
        loadData,
        globalAllFields,
        previousGlobalAllFields,
        globalAdditionalFilters,
        previousGlobalAdditionalFilters,
        chartType,
        previousChartType,
        chartQuery,
        previousChartQuery,
        querySignature,
        previousQuerySignature,
    ]);

    useEffect(() => {
        loaded.current = true;
    }, []);

    return null;
});


export interface BaseCellHandlerProps extends WithTranslation {
    chartId?: number | null;
    chartType: ChartType;
    chartTitle: string;
    chartDescription?: string;
    chartHeight: typeof chartHeightValues[number];
    chartWidth: typeof chartWidthValues[number];
    linearRegression: boolean;
    comparablePeriod: boolean;
    movingAverageDays: number | null;
    movingAverageDaysCenter: boolean;
    chartRef: any;
    scalingFactor?: string;
    barOrientation?: BarOrientation;
    barSortOrder?: BarSortOrder;
    dataLabelPosition?: DataLabelPosition;
    chartQuery: ChartQuery;
    previewOnly: boolean;
    chartSettingsView?: boolean;
    onPreviewFooterChange?: (footerData: ChartFooterData) => void;
}

export interface BaseCellHandlerState {
    data: ChartResponseData | null;
    loading: boolean;
    loaded: boolean;
    errorMsg: string;
    restrictedFieldsWarning: string;
    periodChange: boolean;
    tempInterval: ChartIntervalUnit;
    tempMetricInterval: number;
    queryDuration: string;
}


class BaseCellHandler extends React.PureComponent<BaseCellHandlerProps, BaseCellHandlerState, WithTranslation> {
    endpointUrl: string | null = '';
    requestCancelSource?: CancelTokenSource;
    private _isMounted = false;

    constructor(props: BaseCellHandlerProps) {
        super(props);

        const {intervalUnit, metric_interval} = props.chartQuery;

        this.state = {
            data: null,
            loading: Boolean(props.chartQuery.query_string),
            loaded: false,
            errorMsg: '',
            restrictedFieldsWarning: '',
            periodChange: false,
            tempInterval: intervalUnit,
            tempMetricInterval: metric_interval,
            queryDuration: '',
        };
    }

    componentDidMount() {
        this._isMounted = true;
        this.requestCancelSource = axios.CancelToken.source();
    }

    componentDidUpdate(prevProps: BaseCellHandlerProps, prevState: BaseCellHandlerState) {
        const {chartQuery} = this.props;
        const {chartQuery: prevChartQuery} = prevProps;

        if (
            this.props.chartSettingsView
            && (
                prevProps.onPreviewFooterChange !== this.props.onPreviewFooterChange
                || prevState.queryDuration !== this.state.queryDuration
            )
        ) {
            this.props.onPreviewFooterChange?.({queryDuration: this.state.queryDuration});
        }

        if (prevProps.chartType !== this.props.chartType) {
            this.setStateIfMounted({
                data: null,
                loading: Boolean(chartQuery.query_string),
                loaded: false,
                errorMsg: '',
            });
        }

        if (
            prevChartQuery.intervalUnit !== chartQuery.intervalUnit
            || prevChartQuery.intervalNumber !== chartQuery.intervalNumber
        ) {

            this.setStateIfMounted({tempInterval: chartQuery.intervalUnit});
        }

        if (
            prevChartQuery.metric_interval !== chartQuery.metric_interval
        ) {

            this.setStateIfMounted({tempMetricInterval: chartQuery.metric_interval});
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        // This shows up as an error in the console but actually this is the desired and ok behaviour
        if (this.requestCancelSource !== undefined) {
            this.requestCancelSource.cancel('Component unmounted');
        }
    }

    protected _getRequestParams(
        tempInterval: ChartIntervalUnit,
        tempMetricInterval: number,
        chartQuery: ChartQuery,
    ): ChartQuery {
        const updatedChartQuery = {...chartQuery};

        if (tempInterval !== chartQuery.intervalUnit) {
            updatedChartQuery.intervalUnit = tempInterval;
            updatedChartQuery.intervalNumber = 1;
        }

        if (tempMetricInterval !== chartQuery.metric_interval) {
            updatedChartQuery.metric_interval = tempMetricInterval;
        }

        const {chartHeight, previewOnly} = this.props;

        updatedChartQuery.limit = previewOnly ? 8 : chartHeight * 5;

        return updatedChartQuery;
    }

    protected getRequestParams = memoize<typeof this._getRequestParams>(this._getRequestParams);

    private setStateIfMounted(updater: Partial<BaseCellHandlerState> | ((state: BaseCellHandlerState) => Partial<BaseCellHandlerState>)) {
        if (this._isMounted) {
            this.setState(updater as any);
        }
    }

    protected getNoDataMessage(alertClasses = '') {
        const {chartType} = this.props;
        if (chartType === ChartType.TrendingWords) {
            return (
                <InlineAlert
                    variant="warning"
                    message={this.props.i18n.t('jt_there_are.text')}
                    className={alertClasses}
                />
            );
        }
        return (
            <InlineAlert
                variant="warning"
                message={this.props.i18n.t('jt_this_query.text')}
                className={alertClasses}
            />
        );
    }

    protected loadData = async (
        debounce = true,
        globalFields: AllFields,
        globalFilters: AdditionalFilter[],
    ): Promise<void> => {
        // Don't start a new request if component is unmounted
        if (!this._isMounted) {
            return;
        }

        const {loading, loaded, tempInterval, tempMetricInterval} = this.state;
        const {chartQuery} = this.props;

        if (!this.endpointUrl) {
            console.error('❌ No endpointUrl defined');
            throw new Error(this.props.i18n.t('base_cell_handler_endpoint_url_not_defined.text'));
        }

        if (loading && loaded && this.requestCancelSource !== undefined) {
            this.requestCancelSource.cancel(this.props.i18n.t('base_cell_handler_filters_updated_request_submitted.text'));
            this.requestCancelSource = axios.CancelToken.source();
        }

        let requestArgs = this.getRequestParams(tempInterval, tempMetricInterval, chartQuery);
        requestArgs = sanitizeChartQueryForRequest(requestArgs, this.props.chartType);
        const [mergedFields, mergedFilters] = mergeLocalAndGlobalFilters(
            requestArgs.all_fields, requestArgs.additionalFilters, globalFields, globalFilters,
        );

        requestArgs = produce(requestArgs, (draft) => {
            draft.all_fields = mergedFields;
            draft.additionalFilters = mergedFilters;
        });

        const restrictedFields: string[] = resolveRestrictedFields((window as any).USER_DATA?.restrictedFieldKeys);
        const removedFieldLabels: string[] = [];
        if (restrictedFields.length) {
            const restricted = new Set(restrictedFields);
            requestArgs = produce(requestArgs, (draft) => {
                if (draft.additionalFilters) {
                    draft.additionalFilters = draft.additionalFilters.filter((f: any) => {
                        const fieldName = (f.value || '').replace('all_fields.', '').replace('.keyword', '');
                        if (restricted.has(fieldName)) {
                            removedFieldLabels.push(f.labelClean || f.label || fieldName);
                            return false;
                        }
                        return true;
                    });
                }
                if (draft.all_fields && typeof draft.all_fields === 'object') {
                    for (const key of Object.keys(draft.all_fields)) {
                        const cleanKey = key.replace('all_fields.', '').replace('.keyword', '');
                        if (restricted.has(cleanKey)) {
                            delete (draft.all_fields as any)[key];
                        }
                    }
                }
                if (draft.aggs_item) {
                    const cleanAgg = draft.aggs_item.replace('all_fields.', '').replace('.keyword', '');
                    if (restricted.has(cleanAgg)) {
                        removedFieldLabels.push(cleanAgg);
                        draft.aggs_item = '';
                    }
                }
                if (draft.aggs_item_list && draft.aggs_item_list.length) {
                    const filtered = draft.aggs_item_list.filter((agg: any) => {
                        const cleanAgg = (agg.value || '').replace('all_fields.', '').replace('.keyword', '');
                        if (restricted.has(cleanAgg)) {
                            removedFieldLabels.push(agg.label_clean || agg.label || cleanAgg);
                            return false;
                        }
                        return true;
                    });
                    draft.aggs_item_list = filtered;
                }
                if (draft.metric_aggregations_aggs_item) {
                    const cleanMetric = draft.metric_aggregations_aggs_item.replace('all_fields.', '').replace('.keyword', '');
                    if (restricted.has(cleanMetric)) {
                        removedFieldLabels.push(cleanMetric);
                        draft.metric_aggregations_aggs_item = '';
                    }
                }
            });
        }
        if (removedFieldLabels.length) {
            this.setStateIfMounted({
                restrictedFieldsWarning: `This chart uses restricted fields (${removedFieldLabels.join(', ')}). These fields have been removed. Please edit the chart settings.`,
                loading: false,
                data: null,
            });
            return;
        } else {
            this.setStateIfMounted({restrictedFieldsWarning: ''});
        }

        if (!requestArgs.query_string) {
            this.setStateIfMounted({
                loading: false,
                errorMsg: '',
                data: null,
            });
            return;
        }

        const {chartType} = this.props;

        if (!QUERY_TYPES_WITHOUT_AGG_FIELDS.includes(chartType)) {
            const output = requestArgs.output;
            const requiresAggsItem = output === 'terms_timeline' || output === 'terms' || output === 'terms_metric' || output === 'pivot_terms';
            const requiresAggsItemList = output === 'multi_terms_timeline' || output === 'multi_terms' || output === 'nested_terms';
            const requiresPivotCol = output === 'pivot_terms';

            if (requiresAggsItem && !requestArgs.aggs_item) {
                this.setStateIfMounted({
                    loading: false,
                    errorMsg: '',
                    data: {} as ChartResponseData,
                });
                return;
            }

            if (requiresAggsItemList && (!requestArgs.aggs_item_list || requestArgs.aggs_item_list.length === 0)) {
                this.setStateIfMounted({
                    loading: false,
                    errorMsg: '',
                    data: {} as ChartResponseData,
                });
                return;
            }

            if (requiresPivotCol && !requestArgs.pivot_col_aggs_item) {
                this.setStateIfMounted({
                    loading: false,
                    errorMsg: '',
                    data: {} as ChartResponseData,
                });
                return;
            }

            if (
                requiresPivotCol
                && requestArgs.metric_aggregations_type !== 'count'
                && !requestArgs.metric_aggregations_aggs_item
            ) {
                this.setStateIfMounted({
                    loading: false,
                    errorMsg: '',
                    data: {} as ChartResponseData,
                });
                return;
            }

            if (
                hierarchicalAggregationChartTypes.includes(chartType)
                && (output === 'multi_terms' || output === 'nested_terms')
                && (requestArgs.aggs_item_list?.length ?? 0) < 2
            ) {
                this.setStateIfMounted({
                    loading: false,
                    errorMsg: '',
                    data: {} as ChartResponseData,
                });
                return;
            }
        }

        this.setStateIfMounted({loading: true});

        // Prepare params with JSON-stringified fields to match backend expectations
        const serializedParams = {
            ...requestArgs,
            all_fields: JSON.stringify(requestArgs.all_fields || {}),
            additionalFilters: JSON.stringify(requestArgs.additionalFilters || []),
        };

        const params = {
            params: serializedParams,
            cancelToken: this.requestCancelSource?.token ?? undefined,
        };

        let response: AxiosResponse<ChartResponseData | string>;
        try {
            if (debounce) {
                const debouncedRequest = this.props.chartSettingsView
                    ? chartSettingsPreviewRequestDebounced
                    : chartUpdateRequestDebounced;
                response = await debouncedRequest(this.endpointUrl, params);
            } else {
                response = await chartUpdateRequest(this.endpointUrl, params);
            }
        } catch (error: any) {
            this.setStateIfMounted({loading: false});
            return;
        }

        // Check if component is still mounted before processing response
        if (!this._isMounted) {
            return;
        }

        this.setStateIfMounted({loaded: true});

        // Derive query duration from JSON body if available.
        // For SearchApi, the backend attaches `queryDuration` to each chart_data row.
        // For SearchCountApi and text samples, we expose a top-level `queryDuration` field.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data: any = response?.data;
        let duration: unknown;
        if (Array.isArray(data) && data.length > 0 && data[0]?.queryDuration !== undefined) {
            duration = data[0].queryDuration;
        } else if (data && typeof data === 'object' && data.queryDuration !== undefined) {
            duration = data.queryDuration;
        }

        if (duration !== undefined && duration !== null && String(duration) !== '') {
            this.setStateIfMounted({queryDuration: String(duration)});
        }

        if (!response) {
            this.setStateIfMounted({loading: false});
            return;
        }

        if (!['2', '3'].includes(response.status.toString()[0])) {
            this.processError(response.data, response.status);
        } else {
            this.processData(response.data as ChartResponseData);
        }
    }

    protected processData(data: ChartResponseData): void {
        this.setStateIfMounted({
            data: data,
            errorMsg: '',
            loading: false,
        });
    }

    protected processError(data: unknown, status: number) {
        let errorMsg = this.props.i18n.t('base_cell_handler_something_went_wrong_data_unavailable.text');

        if (status === 400) {
            if (typeof data === 'string') {
                errorMsg = data;
            } else if (data && typeof data === 'object') {
                const errorObj = data as {error?: string; message?: string; details?: Record<string, unknown>};
                if (errorObj.details) {
                    const detailMessages = Object.values(errorObj.details).join(', ');
                    errorMsg = detailMessages || errorObj.error || errorMsg;
                } else if (errorObj.error) {
                    errorMsg = errorObj.error;
                } else if (errorObj.message) {
                    errorMsg = errorObj.message;
                } else {
                    errorMsg = JSON.stringify(data);
                }
            }

            if (errorMsg === this.props.i18n.t('base_cell_handler_too_many_datapoints_requested.text')) {
                this.setStateIfMounted({periodChange: true});
            }
        }

        this.setStateIfMounted({
            errorMsg,
            loading: false,
        });
    }

    private getIntervalLabel(intervalUnit: ChartIntervalUnit): string {
        switch (intervalUnit) {
            case ChartIntervalUnit.Hour:
            case 'h':
                return i18n.t('show_by_hours.text');
            case ChartIntervalUnit.Day:
            case 'd':
                return i18n.t('show_by_days.text');
            case ChartIntervalUnit.Week:
            case 'w':
                return i18n.t('show_by_weeks.text');
            case ChartIntervalUnit.Month:
            case 'M':
                return i18n.t('show_by_months.text');
            case ChartIntervalUnit.Quarter:
            case 'q':
                return i18n.t('show_by_quarters.text');
            case ChartIntervalUnit.Year:
            case 'y':
                return i18n.t('show_by_years.text');
            default:
                return '';
        }
    }

    /* eslint-disable class-methods-use-this */
    protected renderContent(): ReactNode {
        // Implement in subclass
        return null;
    }

    /* eslint-enable class-methods-use-this */
    render() {
        const {
            data,
            loading,
            errorMsg,
            restrictedFieldsWarning,
            periodChange,
            tempInterval,
            tempMetricInterval,
        } = this.state;
        const {
            chartQuery,
            previewOnly,
            chartHeight,
            chartWidth,
        } = this.props;

        const intervalUnit = chartQuery.intervalUnit || defaultIntervalUnit;

        const alertClasses = chartWidth !== 1 && chartHeight !== 1 ? 'mt-4 mx-auto w-75' : 'm-4';

        let content;

        if (!chartQuery.query_string) {
            content = (
                <InlineAlert
                    variant="success"
                    message={this.props.i18n.t('jt_start_writing.text')}
                    className={alertClasses}
                />
            );
        } else if (!loading && errorMsg) {
            content = (
                <div>
                    <InlineAlert
                        variant="error"
                        message={errorMsg}
                        className={alertClasses}
                    />
                    {
                        periodChange && !previewOnly ? (
                            <div className="mt-4 d-flex flex-wrap justify-content-center">
                                {Array.isArray(chartTimelineIntervals) ? chartTimelineIntervals.slice(
                                    chartTimelineIntervals.findIndex((x) => x[0] === intervalUnit) + 1,
                                ).map(([key]) => (
                                    <Button
                                        variant="outline-info"
                                        data-cy={`chart-temp-interval-show-${key}`}
                                        key={key}
                                        className="m-1"
                                        size="sm"
                                        onClick={() => {
                                            this.setStateIfMounted({
                                                errorMsg: '',
                                                periodChange: false,
                                                tempInterval: key,
                                            });
                                        }}
                                    >
                                        {this.getIntervalLabel(key)}
                                    </Button>
                                )) : []}
                            </div>
                        ) : null
                    }
                </div>
            );
        } else if (data && Object.keys(data).length === 0 && !loading) {
            content = this.getNoDataMessage(alertClasses);
        } else if (loading || !data) {
            content = <CellLoader fullHeight={false} />;
        } else {
            content = this.renderContent();
        }

        return (
            <>
                {restrictedFieldsWarning ? (
                    <InlineAlert
                        variant="warning"
                        message={restrictedFieldsWarning}
                        className={alertClasses}
                    />
                ) : (
                    content
                )}

                <GlobalFilterChartRefreshPlugin
                    chartQuery={this.getRequestParams(tempInterval, tempMetricInterval, chartQuery)}
                    chartType={this.props.chartType}
                    loadData={this.loadData}
                    querySignature={`${this.props.chartType}:${JSON.stringify(this.getRequestParams(tempInterval, tempMetricInterval, chartQuery))}`}
                />
            </>
        );
    }
}

export default BaseCellHandler;
